import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Alert,
} from '@mui/material';
import { CardGiftcard as RewardIcon } from '@mui/icons-material';
import corporateService, { CorporateClient, RewardDistribution } from '../../services/corporate.service';

const statusColor: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  COMPLETED: 'success',
  FAILED: 'error',
  PENDING: 'warning',
};

const RewardDistributionsPage: React.FC = () => {
  const [clients, setClients] = useState<CorporateClient[]>([]);
  const [selectedClient, setSelectedClient] = useState<number | ''>('');
  const [distributions, setDistributions] = useState<RewardDistribution[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    corporateService.getAll().then(setClients).catch(() => null);
  }, []);

  useEffect(() => {
    if (!selectedClient) { setDistributions([]); return; }
    setLoading(true);
    setError(null);
    corporateService.getDistributions(selectedClient as number)
      .then(setDistributions)
      .catch(() => setError('Erro ao carregar distribuições'))
      .finally(() => setLoading(false));
  }, [selectedClient]);

  const totalCompleted = distributions.filter(d => d.status === 'COMPLETED').reduce((s, d) => s + Number(d.amount), 0);
  const totalFailed = distributions.filter(d => d.status === 'FAILED').length;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <RewardIcon sx={{ fontSize: 32, color: '#6233A0' }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Distribuições de Recompensas</Typography>
          <Typography variant="body2" color="text.secondary">Histórico de recompensas mensais distribuídas</Typography>
        </Box>
      </Box>

      <FormControl sx={{ minWidth: 300, mb: 3 }}>
        <InputLabel>Empresa</InputLabel>
        <Select
          value={selectedClient}
          label="Empresa"
          onChange={e => setSelectedClient(e.target.value as number)}
        >
          {clients.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
        </Select>
      </FormControl>

      {selectedClient && !loading && distributions.length > 0 && (
        <Box display="flex" gap={3} mb={2}>
          <Typography variant="body2">
            <b>{distributions.filter(d => d.status === 'COMPLETED').length}</b> concluídas —{' '}
            <b>{totalCompleted.toLocaleString('pt-CV')} ECV</b> distribuídos
          </Typography>
          {totalFailed > 0 && (
            <Typography variant="body2" color="error">
              <b>{totalFailed}</b> falhas
            </Typography>
          )}
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : selectedClient ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><b>Funcionário</b></TableCell>
                <TableCell><b>Departamento</b></TableCell>
                <TableCell><b>Período</b></TableCell>
                <TableCell align="right"><b>Valor (ECV)</b></TableCell>
                <TableCell align="center"><b>Estado</b></TableCell>
                <TableCell><b>Data</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {distributions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhuma distribuição encontrada
                  </TableCell>
                </TableRow>
              ) : distributions.map(d => (
                <TableRow key={d.id} hover>
                  <TableCell>{d.employee_nome}</TableCell>
                  <TableCell>{d.departamento || '—'}</TableCell>
                  <TableCell>{d.periodo}</TableCell>
                  <TableCell align="right">{Number(d.amount).toLocaleString('pt-CV')}</TableCell>
                  <TableCell align="center">
                    <Chip label={d.status} size="small" color={statusColor[d.status]} />
                  </TableCell>
                  <TableCell>{new Date(d.created_at).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <RewardIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">Selecciona uma empresa para ver as distribuições</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RewardDistributionsPage;
